import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import { IAM_BASE } from '@/api/paths';
import { ApiError, ApiShapeError, type ApiEnvelope } from '@/lib/task-api';
import type { BankAccount } from '@/lib/task-accounts';
import type { ServerPage } from '@/lib/task-server-list';
import {
  dropTaskFromOpenLists,
  invalidateMarketplaceLists,
  marketplaceKeys,
} from '@/lib/task-query-keys';
import {
  createTask,
  fetchTaskById,
  fetchTaskCount,
  updateTask,
  type CreateTaskInput,
  type UpdateTaskInput,
} from './api';
import type { TaskRow } from './types';

/**
 * TALEP MUTASYONLARI — hepsi listeleri GECERSIZ KILAR.
 *
 * Secenek fabrikalari (`*MutationOptions`) React'ten BAGIMSIZ birer fonksiyon:
 * boylece "mutasyon listeyi gercekten gecersiz kiliyor mu" sorusu bir bilesen
 * kurmadan, dogrudan sinanabiliyor (bkz. src/lib/__tests__/task-query-keys.test.ts).
 * Bu, mobilde tekrar tekrar yasanan "kayit olustu ama listede yok" hatasinin
 * tek gercek korumasi.
 */

export const createTaskMutationOptions = (
  client: QueryClient,
): UseMutationOptions<ApiEnvelope, Error, CreateTaskInput> => ({
  mutationFn: createTask,
  onSuccess: () => {
    invalidateMarketplaceLists(client);
  },
});

export const updateTaskMutationOptions = (
  client: QueryClient,
): UseMutationOptions<ApiEnvelope, Error, UpdateTaskInput> => ({
  mutationFn: updateTask,
  onSuccess: (_data, variables) => {
    // Onay ve iptal talebi Acik Talepler'den cikarir; satir onbellekten HEMEN duser
    // (dropTaskFromOpenLists), ardindan her sey gecersiz kilinir.
    if (variables.taskStatus === 'pending' || variables.taskStatus === 'cancelled') {
      dropTaskFromOpenLists(client, variables.taskId);
    }
    invalidateMarketplaceLists(client);
  },
});

export const useCreateTask = () => {
  const client = useQueryClient();
  return useMutation(createTaskMutationOptions(client));
};

export const useUpdateTask = () => {
  const client = useQueryClient();
  return useMutation(updateTaskMutationOptions(client));
};

/**
 * Piyasa listesinin onbellegindeki satir.
 *
 * Listeden tiklayarak gelen kullanici, detay sayfasinda BOS bir iskelet
 * gormemeli — satir zaten elimizde. Bu yalniz bir TOHUM: sorgu yine de
 * sunucuya gidip fazi tazeliyor (`placeholderData`, `initialData` DEGIL —
 * ikincisi tazelemeyi bastirir ve geri sayim donmus kalirdi).
 */
export const useCachedTask = (taskId: string): TaskRow | undefined => {
  const client = useQueryClient();
  const pages = client.getQueriesData<ServerPage<TaskRow>>({
    queryKey: marketplaceKeys.taskLists(),
  });
  for (const [, page] of pages) {
    // Kimlikler pg int8 -> STRING; sayiya cevirip karsilastirmak yasak.
    const found = page?.items.find((item) => String(item.id) === String(taskId));
    if (found) return found;
  }
  return undefined;
};

/**
 * Tek talep. Tohum varsa aninda gorunur, sunucu yaniti uzerine yazar.
 *
 * `ownership` ZORUNLU: /v1/tasks sahiplik alani gelmezse en dar okumaya duser ve derin
 * baglantida talep bulunamaz. Musteri `{taskUserId}`, buro `{taskPretenderCurrencyUserId}`
 * gonderir; sunucu degeri yine de dogrulanmis cagirana zorlar.
 *
 * `pollMs`: SON GELEN satira gore yoklama araligi (false = yoklama yok). Web'e push gelmiyor;
 * musterinin talep detayi teklif/karar fazinda ve buronun adimlarini beklerken satiri tazeler
 * (2026-10-02). Verilmezse yoklanmaz (buro piyasa detayi bugunku gibi). Yalniz sayfa gorunurken.
 */
export const useTask = (
  taskId: string,
  seed: TaskRow | undefined,
  ownership: Record<string, string>,
  pollMs?: (task: TaskRow | null | undefined) => number | false,
) =>
  useQuery({
    queryKey: [...marketplaceKeys.taskDetail(taskId), ownership],
    queryFn: () => fetchTaskById(taskId, ownership),
    enabled: taskId.length > 0,
    ...(seed === undefined ? {} : { placeholderData: seed }),
    refetchInterval: (query) => (pollMs ? pollMs(query.state.data) : false),
    refetchIntervalInBackground: false,
  });

/* -------------------------------------------------------------------------- */
/* Islem icin banka hesaplari                                                  */
/* -------------------------------------------------------------------------- */

/**
 * DIKKAT — IAM'in bu ucu ORTAK ZARFI KULLANMIYOR: `items`/`page` yerine
 * `{ code, accountList }` donuyor. Kopyala-yapistir bir `items` okumasi burada
 * sessizce bos liste uretirdi.
 */
type AccountsResponse = ApiEnvelope & { accountList?: unknown };

const toAccount = (raw: Record<string, unknown>): BankAccount | null => {
  const code = raw.accountCode;
  const currency = raw.accountCurrency;
  if (typeof code !== 'string' || typeof currency !== 'string') return null;
  return {
    accountCode: code,
    accountCurrency: currency,
    ...(typeof raw.accountLabel === 'string' ? { accountLabel: raw.accountLabel } : {}),
    ...(typeof raw.accountName === 'string' ? { accountName: raw.accountName } : {}),
    ...(typeof raw.accountIbanCode === 'string' ? { accountIbanCode: raw.accountIbanCode } : {}),
  };
};

/**
 * Kullanicinin banka hesaplari — talep/teklif akisinin UYGUNLUK kontrolu icin.
 *
 * Hesap YONETIMI ekranlari ayri bir ozellik (src/features/accounts) ve kendi
 * sorgu agacini kullaniyor. Burada bilincli olarak AYRI bir anahtar
 * (['task-accounts', userId]) var: iki ozelligin birbirinin onbellegini
 * sessizce bosaltmasi, tam olarak nereden geldigi anlasilmayan bir titremeye
 * yol acardi.
 */
export const useTaskAccounts = (userId: string | undefined) =>
  useQuery({
    queryKey: marketplaceKeys.taskAccounts(userId ?? ''),
    enabled: Boolean(userId),
    queryFn: async (): Promise<BankAccount[]> => {
      const { data } = await api.get<AccountsResponse>(`${IAM_BASE}/v1/accounts`, {
        params: { accountUserId: userId },
      });
      // Sessizce bos listeye DUSULMEZ: "hesabin yok" ile "hesaplarini
      // getiremedim" ayni ekrani gostermemeli — birincisi kullaniciya hesap
      // eklemesini soyler, ikincisi tekrar denemesini.
      if (data.code !== 'success') throw new ApiError(data.code ?? 'UNKNOWN_ERROR', data.message);
      if (!Array.isArray(data.accountList)) throw new ApiShapeError('/v1/accounts.accountList dizi degil');
      return data.accountList
        .filter((item): item is Record<string, unknown> => typeof item === 'object' && item !== null)
        .map(toAccount)
        .filter((account): account is BankAccount => account !== null);
    },
  });

/**
 * Sekme rozetleri — her sekme icin TOPLAM kayit sayisi.
 *
 * Neden acilan sekme icin de cekiliyor: liste sayfalanmis geliyor ve `page.returned` yalnizca
 * O SAYFADAKI satir sayisi. Mobilde ekrana basilan sayi tam olarak buydu ve 37 kayitlik listede
 * "20" gosteriyordu -- raporun "sekme sayisi ile liste uyusmuyor" sikayeti.
 *
 * Anahtarlar ['tasks','count',...] altinda, yani her talep mutasyonu bunlari da gecersiz kiliyor.
 *
 * `pollMs`: KARSI TARAFIN adimiyla degisen bir sayac icin yoklama (orn. Acik Talepler'deki
 * odeme seridi — buronun "Odeme alindi"si web'e push ile gelmiyor). Verilmezse yoklanmaz;
 * sekme rozetleri bugunku gibi odak + mutasyonla tazelenir.
 */
export const useTaskCounts = (
  tabs: ReadonlyArray<{ value: string; params: Record<string, string | number | undefined> }>,
  enabled = true,
  pollMs?: number,
): Record<string, number | undefined> => {
  const results = useQueries({
    queries: tabs.map((tab) => ({
      queryKey: marketplaceKeys.taskCount(JSON.stringify(tab.params)),
      queryFn: () => fetchTaskCount(tab.params),
      enabled,
      // Rozet, listeden daha uzun sure bayat kalabilir: bir sayinin bir kac saniye eski olmasi
      // yanlis satir gostermekten cok daha ucuz.
      staleTime: 15_000,
      // Listelerle ayni: sekmeye donuste tazele (lib/task-server-list.ts).
      refetchOnWindowFocus: true,
      // Listelerle ayni: yalniz sayfa GORUNURKEN yoklanir.
      refetchInterval: pollMs ?? false,
      refetchIntervalInBackground: false,
    })),
  });
  return Object.fromEntries(
    tabs.map((tab, index) => [tab.value, results[index]?.data]),
  );
};
